<template>
  <div class="gms-page">
    <FiPageRail title="Group Message Sender">
      <template #sub>
        <div v-if="test_mode" class="gms-test-mode">this feature is in test mode and will not send any SMSs or emails - to enable set 'Test Mode' to False in settings</div>
      </template>
      <template #actions>
        <span class="gms-rail-actions">
          <span class="gms-rail-field">
            <span class="gms-rail-label">User Type:</span>
            <b-form-select v-model="user_type" :options="user_type_options" class="gms-control gms-user-type"/>
          </span>
          <b-button size="sm" variant="secondary" class="mr-1" :to="{ name: 'alert_list' }">
            <b>Back To Alerts</b>
          </b-button>
          <b-button size="sm" variant="success" @click="showInfoModal = true" aria-label="more info" title="more info"><i class="fa fa-info-circle"></i></b-button>
        </span>
      </template>
    </FiPageRail>

    <b-modal v-model="showInfoModal" title="How Group Message Sender Works" size="lg" ok-only ok-title="Close">
      <div class="text-left" style="white-space: pre-line;">{{groupMessageInfoText}}</div>
    </b-modal>

    <div v-if="needs_window" class="gms-window">
      <span class="gms-window-quick">
        <b-button size="sm" :variant="is_quick_window('next_4h') ? 'secondary' : 'outline-secondary'" :aria-pressed="is_quick_window('next_4h') ? 'true' : 'false'" @click="set_quick_window('next_4h')">Next 4 hours</b-button>
        <b-button size="sm" :variant="is_quick_window('rest_of_today') ? 'secondary' : 'outline-secondary'" :aria-pressed="is_quick_window('rest_of_today') ? 'true' : 'false'" @click="set_quick_window('rest_of_today')">Rest of today</b-button>
        <b-button size="sm" :variant="is_quick_window('tomorrow') ? 'secondary' : 'outline-secondary'" :aria-pressed="is_quick_window('tomorrow') ? 'true' : 'false'" @click="set_quick_window('tomorrow')">Tomorrow</b-button>
      </span>
      <span class="gms-window-fields">
        <span class="gms-window-field">
          <span class="gms-window-field-label">From</span>
          <b-form-input v-model="start_date" type="date" size="sm" class="gms-date-input" :min="min_date" :max="max_date" aria-label="window start date"/>
          <GmsTimeInput v-model="start_time" :native="mobileView" input-class="gms-time-input" label="window start time"/>
        </span>
        <span class="gms-window-field">
          <span class="gms-window-field-label">To</span>
          <b-form-input v-model="end_date" type="date" size="sm" class="gms-date-input" :min="min_date" :max="max_date" aria-label="window end date"/>
          <GmsTimeInput v-model="end_time" :native="mobileView" input-class="gms-time-input" label="window end time"/>
        </span>
      </span>
      <b-button class="gms-find-button" variant="primary" :disabled="!!window_error" v-on:click.prevent="load_data()">Find Users</b-button>
      <div v-if="window_error" class="gms-window-error">{{window_error}}</div>
      <div v-else-if="window_is_stale" class="gms-window-error">the window changed - click Find Users to update the list</div>
      <div v-else-if="window_summary_text" class="gms-window-summary">{{window_summary_text}}</div>
    </div>

    <div class="gms-compose">
      <div class="gms-compose-format">
        <span class="gms-rail-label">Message Format:</span>
        <b-form-select v-model="message_type" :options="message_type_options" class="gms-control gms-format-select"/>
      </div>
      <div class="gms-compose-editor">
        <PfsRichEditor ref="messagBodyRef" v-model="message_body" placeholder="Enter a message here."/>
        <PfsFieldHelp
          :fields="pfsGroupFields"
          :editor-ref="$refs.messagBodyRef"
          title="Available Variables"
          tenant-type="CM"
        />
      </div>
    </div>

    <div class="gms-filters">
      <div class="gms-filter gms-filter-count">Showing: {{items.length}} of {{matching_count}} ({{selected_ids.length}} selected)</div>
      <div v-if="employment_status_options.length > 1" class="gms-filter">
        <span class="gms-rail-label">Status:</span>
        <GmsMultiSelect v-model="employment_status_filters" :options="employment_status_count_options" plural="Statuses" width="auto"/>
      </div>
      <div v-if="tag_options.length > 1" class="gms-filter">
        <span class="gms-rail-label">Tag:</span>
        <b-form-select v-model="tag_filter" :options="tag_options" class="gms-control"/>
      </div>
      <div v-if="job_title_options.length > 1" class="gms-filter">
        <span class="gms-rail-label">Job:</span>
        <GmsMultiSelect v-model="job_title_filters" :options="job_title_count_options" plural="Job Titles" width="auto"/>
      </div>
      <div v-if="location_options.length > 1" class="gms-filter">
        <span class="gms-rail-label">Location:</span>
        <GmsMultiSelect v-model="company_location_filters" :options="location_count_options" plural="Locations" width="auto"/>
      </div>
      <div class="gms-filter">
        <span class="gms-rail-label">Search:</span>
        <b-form-input class="gms-control gms-search" v-model="search_filter" placeholder="name, phone, location"/>
      </div>
    </div>

    <div v-if="cardView" class="gms-cards">
      <div v-if="loading" class="text-center text-danger my-3"><b-spinner class="align-middle"></b-spinner></div>
      <div v-else-if="items.length == 0" class="gms-empty">{{empty_text}}</div>
      <div v-else v-for="item in items" :key="item.recipient_id" class="gms-card">
        <b-form-checkbox v-model="selected_ids" :value="item.recipient_id"/>
        <div class="gms-card-body" @click="toggle_one(item)">
          <div class="gms-card-name">{{item.name}}</div>
          <div class="gms-card-meta">
            <span v-if="item.job_title">{{item.job_title}}</span>
            <span v-if="item.company_location">{{item.company_location}}</span>
            <span v-if="item.tags">{{item.tags}}</span>
          </div>
          <div class="gms-card-contact">{{message_type == 'EMAIL' ? item.email : item.phone}}</div>
        </div>
        <b-button size="sm" variant="success" :disabled="is_sending(item.recipient_id)" v-on:click.prevent="send_to_user(item)">
          <b-spinner small v-if="is_sending(item.recipient_id)"/>
          <i class="fa fa-check" v-else-if="just_sent(item.recipient_id)"/>
          <template v-else>Send</template>
        </b-button>
      </div>
    </div>

    <div v-else class="gms-table">
      <b-table :busy="loading" sticky-header="70vh" bordered show-empty :empty-text="empty_text" :items="items" :fields="fields"
               :tbody-tr-class="'gms-row'" @row-clicked="toggle_one"
               :current-page="currentPage"
               :per-page="perPage">

        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>

        <!-- no select-all in the header: the bar at the bottom has Select all / Clear all. this
             bootstrap-vue (2.0.0-rc.28) only knows the older slot names, "<key>" and "HEAD_<key>" -->
        <template slot="selected" slot-scope="row">
          <b-form-checkbox v-model="selected_ids" :value="row.item.recipient_id"/>
        </template>

        <template slot="first_driver_start_time" slot-scope="row">
            {{row.item.first_driver_start_time | formatDateTimeSmall}}
        </template>

        <template slot="last_driver_end_time" slot-scope="row">
          {{row.item.last_driver_end_time | formatDateTimeSmall}}
        </template>

        <template slot="actions" slot-scope="row">
          <b-button size="sm" variant="success" :disabled="is_sending(row.item.recipient_id)" v-on:click.prevent="send_to_user(row.item)">
            <b-spinner small v-if="is_sending(row.item.recipient_id)"/>
            <i class="fa fa-check" v-else-if="just_sent(row.item.recipient_id)"/>
            <template v-else>Send</template>
          </b-button>
        </template>
      </b-table>
      <b-row>
        <b-col>
          <b-pagination
              v-model="currentPage"
              :total-rows="items.length"
              :per-page="perPage"
              align="center"
          >
          </b-pagination>
        </b-col>
      </b-row>
    </div>

    <div class="gms-send-bar">
      <b-form-checkbox :checked="all_shown_selected" :indeterminate="some_shown_selected && !all_shown_selected" @change="toggle_all_shown">
        {{all_shown_selected ? 'Clear all' : 'Select all'}}
      </b-form-checkbox>
      <b-button variant="success" class="gms-send-button" :disabled="selected_ids.length == 0 || items_full.length == 0 || is_sending('group') || !!window_error || window_is_stale" v-on:click.prevent="send_to_group()">
        <b-spinner small v-if="is_sending('group')"/>
        <template v-else-if="just_sent('group')"><i class="fa fa-check"/> Sent</template>
        <template v-else>Send {{message_type}} ({{selected_ids.length}})</template>
      </b-button>
    </div>
  </div>
</template>

<script>

import {get_groupsender_user_list, send_message_to_group, send_message_to_user} from '../networking/apis'
      import {mapState} from "vuex";
      import moment from "moment";
      import PfsFieldHelp from './PfsFieldHelp.vue'
      import PfsRichEditor from './PfsRichEditor.vue'
      import GmsMultiSelect from './GmsMultiSelect.vue'
import GmsTimeInput from './GmsTimeInput.vue'
      import { GROUP_MESSAGE_FIELDS } from '../utils/pfs_fields'
      import { MAX_WINDOW_HORIZON_DAYS, compose_window_value, format_window_value, quick_window, roll_overnight, window_date, window_error, window_summary, window_time, window_value_to_iso } from '../utils/gms_window'
      import gms_sticky_filters from '../mixins/gms_sticky_filters'
import { NOT_SET, not_set_option } from '../utils/gms_filters'
      import gms_page from '../mixins/gms_page'
      import mobile_view from '../mixins/vsm_mobile_view'

      export default {
        name: 'group_message_sender',
        components: {
          PfsFieldHelp,
          PfsRichEditor,
          GmsMultiSelect,
          GmsTimeInput,
      },

      mixins: [gms_page, gms_sticky_filters, mobile_view],

      data() {
        return {
          test_mode: false,
          // false until a load has actually returned, so the empty list can say which it is
          list_loaded: false,
          driver_available: '',
          msg: 'users',
          search_filter: '',
          showInfoModal: false,
          groupMessageInfoText: `Send an SMS or email to a group of users, or to one person at a time.

1. Choose a Message Format (SMS or Email). This also decides who's eligible: only users with a phone number show up for SMS, only users with an email address show up for Email.

2. Pick a User Type in the header (Portal Users, All Drivers, Available Drivers, or Unavailable Drivers). The list loads automatically - except for Available/Unavailable Drivers, where a Time Available range and a "Find Users" button appear so you can pull the list for a specific window. The quick buttons fill in common windows.

3. Write your message in the editor. Click "Available Variables" to insert placeholders (like the recipient's name) that get filled in individually when the message is sent.

4. Narrow the list with the Tag, Job Title, or Location filters if you want. You can check several job titles or locations at once. Changing a filter checks everyone it shows.

5. Uncheck anyone you want to leave out, or use "Select all" in the send bar to clear everyone shown and then check just the people you want. The Search box only helps you find people - it never changes who is checked, so people checked earlier stay checked while you search.

6. Click "Send SMS"/"Send Email" in the bar at the bottom to message only the people who are checked (the button shows how many), or use the "Send" button on an individual row to message just that person.

7. Your filters are remembered for next time.

8. If Test Mode is on, no real messages are sent.`,
          message_type: 'SMS',
          message_body: '',
          pfsGroupFields: GROUP_MESSAGE_FIELDS,
          job_title_filters: [],
          job_title_options: [],
          employment_status_filters: [],
          employment_status_options: [],
          selected_ids: [],
          company_location_filters: [],
          location_options: [],
          user_type: 'users',
          tag_filter: '',
          tag_options: [],
          driver_tag_filter: '',
          items: [],
          items_full: [],
          summary: [],
          loading: true,
          totalRows: 1,
          currentPage: 1,
          perPage: 400,
          pages: [],
          user_type_options: [
            { value: '', text: 'All Users' },
            { value: 'users', text: 'Portal Users' },
            { value: 'drivers', text: 'All Drivers' },
            { value: 'available_drivers', text: 'Available Drivers' },
            // the backend (group_message_sender.py) checks user_type == 'unavailable_drivers',
            // not 'booked_drivers' - fixed to match so this option actually returns results
            { value: 'unavailable_drivers', text: 'Unavailable Drivers' },
          ],
          message_type_options: [
            { value: 'SMS', text: 'SMS' },
            { value: 'EMAIL', text: 'Email' },
          ],
          fields_all: [],
          sending_ids: [],
          loaded_window: '',
          sent_ids: [],
          fields: [
            { key: 'selected', label: '', sortable: false},
            { key: 'type', label: 'Type', sortable: true},
            { key: 'email', label: 'Email Address', sortable: true},
            { key: 'name', label: 'Name', sortable: true},
            { key: 'tags', label: 'Tags', sortable: true},
            { key: 'job_title', label: 'Job Title', sortable: true},
            { key: 'company_location', label: 'Location', sortable: true},
            { key: 'phone', label: 'Phone #', sortable: true},
            // { key: 'first_driver_start_time', label: 'Driver Start Time', sortable: true},
            // { key: 'last_driver_end_time', label: 'Driver End Time', sortable: true},
            { key: 'actions', label: '', sortable: true},
          ],
          // the quick window in effect ('next_4h' / 'rest_of_today' / 'tomorrow'), or '' once the
          // times are edited by hand. this is what gets remembered, not the times themselves: a saved
          // "1pm to 6pm Friday" would come back as a window in the past.
          window_preset: '',
          driverStartTime: format_window_value(moment()),
          driverEndTime: format_window_value(moment().add(12, 'hours')),
        }
      },

      async created() {
        this.fields_all = this.fields
        await this.load_sticky_filters()
        await this.check_cols()
        await this.load_data()
      },
      methods: {
        // highlights whichever chip matches the window on screen, to the minute
        // reads the preset the chip set. recomputing quick_window() against moment() meant the
        // highlight fell off as soon as the clock minute rolled over (FI-2169 review)
        // only a failure drags the user to the top of the page. a success says so on the button
        // that was clicked, right where they are looking
        // per-button state: a spinner while it sends, then a check mark for a moment. 'group' is the
        // send bar's own button; a row uses that person's recipient_id
        // a set, not one scalar: a row send and the group send used to clear each other's spinner,
        // re-enabling a button whose request was still open and inviting a double send (FI-2169 review)
        async send_to_group() {
          // an empty body used to go out as an empty text, with nothing on screen either way
          if (!(this.message_body || '').trim()) {
            this.announce('write a message first', 'danger', 5000)
            return
          }
          const count = this.selected_ids.length
          this.start_sending('group')
          const request_body = {
              "user_type": this.user_type,
              "driverStartTime": window_value_to_iso(this.driverStartTime),
              "driverEndTime": window_value_to_iso(this.driverEndTime),
              "tag_filter": this.tag_filter,
              "job_title_filters": this.job_title_filters,
              "company_location_filters": this.company_location_filters,
              "employment_status_filters": this.employment_status_filters,
              "recipient_ids": this.selected_ids,
              "message_body": this.message_body,
              "message_type": this.message_type
          }
          try {
            await send_message_to_group(this.user.tenant, request_body)
          } catch (error) {
            // a failed send used to leave the "sending..." banner up forever
            this.announce('the messages could not be sent, try again or contact support', 'danger', 8000)
            return
          } finally {
            this.stop_sending('group')
          }
          this.flash_sent('group')
          this.announce(this.sent_message(count), 'success', 5000)
        },
        async send_to_user(user) {
          if (!(this.message_body || '').trim()) {
            this.announce('write a message first', 'danger', 5000)
            return
          }
          this.start_sending(user.recipient_id)
          const request_body = {
              "user": user,
              "message_body": this.message_body,
              "message_type": this.message_type
          }
          try {
            await send_message_to_user(this.user.tenant, request_body)
          } catch (error) {
            this.announce('the message could not be sent, try again or contact support', 'danger', 8000)
            return
          } finally {
            this.stop_sending(user.recipient_id)
          }
          this.flash_sent(user.recipient_id)
          this.announce(this.sent_message(1, user.name), 'success', 5000)
        },
        // the send bar's count is what was checked, which is what the backend messages
        async load_data() {
          this.loading = true
          try {
            const {value: { data = [] } } = await get_groupsender_user_list(this.user.tenant, this.user_type, window_value_to_iso(this.driverStartTime), window_value_to_iso(this.driverEndTime))
            this.items = data.results
            this.items_full = data.results
            this.job_title_options = data.summary.job_titles
            this.tag_options = data.summary.tags
            this.location_options = data.summary.locations
            this.employment_status_options = data.summary.employment_statuses || []
            this.test_mode = data.summary.test_mode
            // a remembered value this list doesn't offer would sit in the toggle with no box to clear it
            this.apply_saved_filter_options()
            // same for the Tag select, which is hidden when the loaded rows carry no tags
            if (!this.tag_options.some(option => option.value == this.tag_filter))
              this.tag_filter = ''
            this.filter_results()
            this.list_loaded = true
            // what the rows on screen were actually computed against
            this.loaded_window = this.needs_window ? this.driverStartTime + '|' + this.driverEndTime : ''
          } catch(e) {
            this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
          }
          this.loading = false
        },
        // everyone passing the format/tag/status/job title/location filters; skip leaves one filter
        // out so that dropdown can count what each of its options would add
        // "Not set" (NOT_SET) picks the rows with nothing in the field; the backends apply the same rule
        // statuses come from a fixed list, so they match exactly -- same rule the backend applies
        // a value nobody in the current list has is noise, so it's left out -- except when it's
        // already checked, which has to stay visible to be uncheckable
        filter_results(keep_selection) {
          this.items = this.matching_items()
          // changing a real filter re-selects everyone it matches; search only narrows the view
          if (!keep_selection)
            this.selected_ids = this.items.map(item => item.recipient_id)
          if (this.search_filter != '') {
            const val = this.search_filter
            this.items = this.items.filter(item => (this.contains(item.name, val) || this.contains(item.company_location, val) || this.contains(item.job_title, val) || this.contains(item.phone, val) || this.contains(item.email, val) || this.contains(item.tags, val)))
          }
        },
        // the whole card is a tap target on a phone, where the checkbox alone is a small one
      },
      computed:{
        ...mapState({
          user: state => state.profile.user
        }),
        // "nothing matched" and "you haven't searched yet" look identical otherwise
        // editing the window after a search does not reload the list, but the send re-sends the
        // window and the backend rebuilds the recipients from it -- so the people who get the
        // message would not be the people on screen (FI-2169 review)
        // date and time are separate controls: a dispatcher covering tonight thinks "today, 6 to
        // midnight". the end rolls onto the next day by itself when the window crosses midnight,
        // and the end date control moves with it so the roll is visible.
        start_date: {
          get() { return window_date(this.driverStartTime) },
          set(value) {
            this.window_preset = ''
            const days = moment(this.end_date).diff(moment(this.start_date), 'days')
            this.driverStartTime = compose_window_value(value, this.start_time)
            // dragging the start date carries the window with it
            this.driverEndTime = compose_window_value(moment(value).add(days, 'days').format('YYYY-MM-DD'), this.end_time)
          },
        },
        start_time: {
          get() { return window_time(this.driverStartTime) },
          set(value) {
            this.window_preset = ''
            this.driverStartTime = compose_window_value(this.start_date, value)
            this.driverEndTime = roll_overnight(this.driverStartTime, this.driverEndTime)
          },
        },
        end_date: {
          get() { return window_date(this.driverEndTime) },
          set(value) {
            this.window_preset = ''
            this.driverEndTime = compose_window_value(value, this.end_time)
          },
        },
        end_time: {
          get() { return window_time(this.driverEndTime) },
          set(value) {
            this.window_preset = ''
            this.driverEndTime = roll_overnight(this.driverStartTime, compose_window_value(this.end_date, value))
          },
        },
        // both lists end with "Not set"; like any value, it only shows when someone in the list has it
        // everyone who passes the format and the filters -- the "of" in "Showing X of Y". the loaded
        // total used to go there, so filters that hid half the list read as if select-all had missed
        // people. X is what the search box leaves of these.
      },
      watch: {
        user: {
          deep: true,
          handler() {
            this.load_data()
          }
        },
        user_type() {
          this.check_cols()
          // created() loads once after the restore; don't fetch twice
          if (this.restoring_sticky_filters)
            return
          this.save_sticky_filters()

          if (this.user_type.indexOf("_drivers") == -1) {
            this.load_data()
          } else {
            // Available/Booked Drivers wait for a window and a Find Users click. the options have
            // to go with the rows: the previous user type's job titles used to stay in the
            // dropdowns, counting (0) against a list that had just been emptied.
            this.items = []
            this.items_full = []
            this.selected_ids = []
            this.job_title_options = []
            this.location_options = []
            this.employment_status_options = []
            this.list_loaded = false
            this.tag_options = []
            // tag_filter is a plain select, so prune_to_options never touches it. leaving it set
            // while its dropdown is hidden filtered everyone out with no way to clear it short of
            // a reload (FI-2169 review)
            this.tag_filter = ''
          }
        },
      }
    }
</script>
